import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

export default function SentinelUserList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    memberId: "",
    userName: "",
  });

  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/sentinel_users`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!usersRes.ok) throw new Error("Failed to fetch sentinel users");

      const dashboardResult = await dashboardRes.json();
      const usersResult = await usersRes.json();

      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const rawUsers = usersResult?.data || usersResult?.users || [];

      const normalized = rawUsers.map((user, index) => {
        const rawApprove = String(
          user.fld_Approve ?? user.status ?? "pending"
        ).toLowerCase();

        const rawActive = Number(user.is_active ?? 0);

        let status = "pending";

        if (rawApprove === "approved" && rawActive === 1) {
          status = "approved";
        } else if (
          rawApprove === "disapproved" ||
          rawApprove === "rejected" ||
          rawActive === 0
        ) {
          status = "disapproved";
        } else if (rawApprove === "pending") {
          status = "pending";
        }

        return {
          id: user.SINo ?? user.SlNo ?? index + 1,
          member_id: user.fld_memberId ?? user.member_id ?? "",
          guard_id: user.fld_guard_id ?? "",
          name: user.fld_name ?? user.name ?? "",
          username: user.fld_username ?? user.username ?? "",
          email: user.fld_mail ?? user.email ?? "",
          password: user.fld_pass ?? user.password ?? "",
          password_display: "********",
          role: user.fld_category ?? user.role ?? "Guard",
          designation: user.fld_designation ?? user.designation ?? "",
          year_starts: user.fld_year_starts ?? "",
          year_ends: user.fld_year_ends ?? "",
          dept: user.fld_dept ?? "",
          phone: user.fld_phone ?? "",
          approve_raw: user.fld_Approve ?? "",
          is_active: rawActive,
          status,
        };
      });

      setUsers(normalized);
    } catch (error) {
      console.error("Sentinel user list fetch error:", error);
      setErrorMessage(error.message || "Failed to load user list.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !search ||
        String(user.member_id).toLowerCase().includes(search) ||
        String(user.name).toLowerCase().includes(search) ||
        String(user.username).toLowerCase().includes(search) ||
        String(user.email).toLowerCase().includes(search);

      const matchesRole = !roleFilter || user.role === roleFilter;
      const matchesStatus = !statusFilter || user.status === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const handleApproval = async (memberId, action, checked) => {
    let newStatus = "pending";

    if (action === "approve" && checked) newStatus = "approved";
    if (action === "disapprove" && checked) newStatus = "disapproved";

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}/sentinel_users/${memberId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!res.ok) throw new Error("Failed to update status");

      setUsers((prev) =>
        prev.map((user) =>
          user.member_id === memberId
            ? {
                ...user,
                status: newStatus,
                approve_raw:
                  newStatus === "approved"
                    ? "Approved"
                    : newStatus === "disapproved"
                    ? "Disapproved"
                    : "Pending",
                is_active: newStatus === "approved" ? 1 : 0,
              }
            : user
        )
      );

      setSuccessMessage("User status updated successfully!");
    } catch (error) {
      console.error("Status update error:", error);
      setErrorMessage(error.message || "Failed to update status.");
    }
  };

  const confirmDelete = (memberId, userName) => {
    setDeleteModal({
      open: true,
      memberId,
      userName,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      open: false,
      memberId: "",
      userName: "",
    });
  };

  const handleDelete = async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}/sentinel_users/${deleteModal.memberId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error("Failed to delete user");

      setUsers((prev) =>
        prev.filter((user) => user.member_id !== deleteModal.memberId)
      );

      setSuccessMessage("User deleted successfully!");
      closeDeleteModal();
    } catch (error) {
      console.error("Delete error:", error);
      setErrorMessage(error.message || "Failed to delete user.");
    }
  };

  const addNewUser = () => navigate("/sentinel_add_user");

  const editUser = (memberId) => {
    navigate(
      `/sentinel_add_user?mode=edit&member_id=${encodeURIComponent(memberId)}`
    );
  };

  const managePermissions = (memberId) => {
    navigate(
      `/sentinel_guard_per?mode=edit&member_id=${encodeURIComponent(memberId)}`
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100">
        <div className="mb-3 h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
        <p className="text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading users…
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-user-list"
    >
      <PageHeader onAddUser={addNewUser} />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      <UsersTable
        users={filteredUsers}
        onEdit={editUser}
        onPermission={managePermissions}
        onDelete={confirmDelete}
        onApproval={handleApproval}
      />

      {deleteModal.open ? (
        <DeleteModal
          userName={deleteModal.userName}
          onClose={closeDeleteModal}
          onConfirm={handleDelete}
        />
      ) : null}
    </Layout>
  );
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function PageHeader({ onAddUser }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal tracking-[-0.3px] text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel User Management
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Manage all sentinel users and their permissions
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white px-7 py-6 shadow-sm">
        <div>
          <h3
            className="mb-1 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            User List
          </h3>

          <p className="text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Search, filter, approve, edit, and remove users
          </p>
        </div>

        <button
          type="button"
          onClick={onAddUser}
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:opacity-95"
          style={{
            fontFamily: FONT_UI,
            background: skin.gradient,
          }}
        >
          + Add New User
        </button>
      </div>
    </>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 rounded-xl border px-4 py-3 text-sm font-medium ${
        isSuccess
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FilterBar({
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
}) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-slate-100 bg-white px-6 py-5 shadow-sm lg:grid-cols-[2fr_1fr_1fr]">
      <Input
        type="text"
        placeholder="Search by name, username, email, or member ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
        <option value="">All Roles</option>
        <option value="Admin">Admin</option>
        <option value="Supervisor">Supervisor</option>
        <option value="Guard">Guard</option>
        <option value="Security">Security</option>
      </Select>

      <Select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="">All Status</option>
        <option value="approved">Approved</option>
        <option value="pending">Pending</option>
        <option value="disapproved">Disapproved</option>
      </Select>
    </div>
  );
}

function UsersTable({ users, onEdit, onPermission, onDelete, onApproval }) {
  const skin = useSkin();

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse">
          <thead>
            <tr style={{ background: skin.gradient }}>
              {[
                "Member ID",
                "User",
                "Username",
                "Password",
                "Role",
                "Status",
                "Approval",
                "Actions",
              ].map((head) => (
                <th
                  key={head}
                  className="px-5 py-4 text-left text-[13px] font-bold tracking-wide text-white"
                  style={{ fontFamily: FONT_UI }}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-5 py-9 text-center text-sm text-slate-500"
                  style={{ fontFamily: FONT_UI }}
                >
                  No users found in the database.
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr
                  key={`${user.member_id}-${index}`}
                  className="border-b border-slate-100 transition hover:bg-slate-50"
                >
                  <td className="px-5 py-4 align-middle text-sm text-slate-700">
                    <span
                      className="font-bold"
                      style={{
                        color: skin.primary,
                        fontFamily: FONT_UI,
                      }}
                    >
                      {user.member_id}
                    </span>
                  </td>

                  <td className="px-5 py-4 align-middle">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                        style={{
                          background: skin.gradient,
                          fontFamily: FONT_UI,
                        }}
                      >
                        {String(user.name || "U").charAt(0).toUpperCase()}
                      </div>

                      <div>
                        <div
                          className="text-sm font-semibold text-slate-900"
                          style={{ fontFamily: FONT_UI }}
                        >
                          {user.name || "—"}
                        </div>

                        <div
                          className="text-xs text-slate-500"
                          style={{ fontFamily: FONT_UI }}
                        >
                          {user.email || "—"}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td
                    className="px-5 py-4 align-middle text-sm text-slate-700"
                    style={{ fontFamily: FONT_UI }}
                  >
                    {user.username || "—"}
                  </td>

                  <td className="px-5 py-4 align-middle font-mono text-sm tracking-[2px] text-slate-700">
                    {user.password_display}
                  </td>

                  <td className="px-5 py-4 align-middle">
                    <RoleBadge role={user.role} />
                  </td>

                  <td className="px-5 py-4 align-middle">
                    <StatusBadge status={user.status} />
                  </td>

                  <td className="px-5 py-4 align-middle">
                    <div className="flex flex-col gap-2">
                      <label
                        className="flex cursor-pointer items-center gap-2 text-[13px] text-green-700"
                        style={{ fontFamily: FONT_UI }}
                      >
                        <input
                          type="checkbox"
                          checked={user.status === "approved"}
                          onChange={(e) =>
                            onApproval(
                              user.member_id,
                              "approve",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                        />
                        <span>Approve</span>
                      </label>

                      <label
                        className="flex cursor-pointer items-center gap-2 text-[13px] text-red-600"
                        style={{ fontFamily: FONT_UI }}
                      >
                        <input
                          type="checkbox"
                          checked={user.status === "disapproved"}
                          onChange={(e) =>
                            onApproval(
                              user.member_id,
                              "disapprove",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                        />
                        <span>Disapprove</span>
                      </label>
                    </div>
                  </td>

                  <td className="px-5 py-4 align-middle">
                    <div className="flex flex-wrap gap-2">
                      <ActionButton
                        label="Edit"
                        className="bg-sky-500 hover:bg-sky-600"
                        onClick={() => onEdit(user.member_id)}
                      />

                      <ActionButton
                        label="Permissions"
                        className="bg-amber-500 hover:bg-amber-600"
                        onClick={() => onPermission(user.member_id)}
                      />

                      <ActionButton
                        label="Delete"
                        className="bg-red-500 hover:bg-red-600"
                        onClick={() => onDelete(user.member_id, user.name)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
        <div className="text-[13px] text-slate-500" style={{ fontFamily: FONT_UI }}>
          Showing 1 to {users.length} of {users.length} entries
        </div>

        <div className="flex gap-2">
          <PaginationButton>Previous</PaginationButton>
          <PaginationButton active>1</PaginationButton>
          <PaginationButton>Next</PaginationButton>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const lower = String(role || "").toLowerCase();

  let classes = "bg-green-50 text-green-700";

  if (lower === "admin") {
    classes = "bg-sky-100 text-sky-700";
  } else if (lower === "supervisor") {
    classes = "bg-purple-100 text-purple-700";
  } else if (lower === "security") {
    classes = "bg-lime-100 text-lime-700";
  }

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${classes}`}
      style={{ fontFamily: FONT_UI }}
    >
      {role || "—"}
    </span>
  );
}

function StatusBadge({ status }) {
  let classes = "bg-orange-50 text-orange-700";

  if (status === "approved") {
    classes = "bg-green-100 text-green-700";
  } else if (status === "disapproved") {
    classes = "bg-red-100 text-red-600";
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${classes}`}
      style={{ fontFamily: FONT_UI }}
    >
      <span>●</span>
      <span>{String(status || "pending").charAt(0).toUpperCase() + String(status || "pending").slice(1)}</span>
    </span>
  );
}

function ActionButton({ label, className = "", onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-3 py-2 text-xs font-bold text-white transition ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {label}
    </button>
  );
}

function DeleteModal({ userName, onClose, onConfirm }) {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/45 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl"
      >
        <h3
          className="mb-3 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Confirm Delete
        </h3>

        <p className="mb-3 text-sm text-slate-700" style={{ fontFamily: FONT_UI }}>
          Are you sure you want to delete user <strong>{userName}</strong>?
        </p>

        <p className="mb-5 text-[13px] text-red-600" style={{ fontFamily: FONT_UI }}>
          This action cannot be undone.
        </p>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl border border-red-500 bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600"
            style={{ fontFamily: FONT_UI }}
          >
            Delete User
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function PaginationButton({ active = false, children }) {
  return (
    <button
      type="button"
      className={`rounded-lg border px-3 py-2 text-[13px] font-semibold transition ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}